import { useEffect, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { StatusChip } from '@/components/common/StatusChip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Flag, AlertTriangle, CheckCircle2, CalendarClock, Layers, RefreshCcw, ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { todayISO } from '@/lib/dates';

/**
 * MyMilestonesStrip — milestones the current user owns (scope=open), with
 * inline Done + Shift actions so owners can act without leaving My Week.
 * Shifting requires a slip reason — that feeds the "Why projects slipped"
 * trend in Reports → Project Health.
 */
export function MyMilestonesStrip({ testId = 'my-milestones-strip' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [shiftFor, setShiftFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/master-projects/my/milestones?scope=open');
      setData(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) return null;
  if (!data || data.total === 0) return null;

  const overdue = data.rows.filter(r => r.is_overdue);
  const others = data.rows.filter(r => !r.is_overdue);

  const markDone = async (m) => {
    const key = `${m.project_id}:${m.milestone_index}`;
    setBusyKey(key);
    try {
      await api.post(`/master-projects/${m.project_id}/milestones/act`, {
        milestone_index: m.milestone_index,
        milestone_name: m.milestone_name,
        action: 'done',
      });
      toast.success(`“${m.milestone_name}” marked done.`);
      load();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusyKey(null); }
  };

  return (
    <div className="card p-3 elev-1" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-md flex items-center justify-center flex-none"
               style={{ background: 'hsl(var(--status-in-progress-bg))' }}>
            <Flag size={14} className="text-[hsl(var(--status-in-progress))]" />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-semibold text-[15px]">Milestones you own</div>
            <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] leading-tight">
              {data.total} open · {overdue.length > 0
                ? <span className="text-[hsl(var(--danger))] font-medium">{overdue.length} overdue</span>
                : 'nothing overdue'}
            </div>
          </div>
        </div>
        <button onClick={load} className="p-1 rounded hover:bg-[hsl(var(--surface-2))]" title="Refresh" data-testid={`${testId}-refresh`}>
          <RefreshCcw size={12} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {[...overdue, ...others].map((m) => (
          <MilestoneCard key={`${m.project_id}-${m.milestone_index}`} m={m}
                         busy={busyKey === `${m.project_id}:${m.milestone_index}`}
                         onDone={() => markDone(m)}
                         onShift={() => setShiftFor(m)} />
        ))}
      </div>

      {shiftFor && (
        <ShiftMilestoneDialog
          m={shiftFor}
          reasons={data.slip_reasons || []}
          onClose={() => setShiftFor(null)}
          onDone={() => { setShiftFor(null); load(); }}
        />
      )}
    </div>
  );
}

function MilestoneCard({ m, busy, onDone, onShift }) {
  const overdue = m.is_overdue;
  return (
    <div className={`border rounded-lg p-2.5 hover:border-[hsl(var(--border-strong))] transition ${busy ? 'opacity-60' : ''}`}
         style={{
           borderColor: overdue ? 'hsl(var(--danger))' : 'hsl(var(--border))',
           background: 'hsl(var(--card))',
         }}
         data-testid={`my-milestone-${m.project_id}-${m.milestone_name.replace(/\s+/g, '_')}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-medium leading-snug truncate">{m.milestone_name}</div>
          <div className="text-[11px] text-[hsl(var(--foreground-muted))] flex items-center gap-1 truncate mt-0.5">
            <Layers size={10} />
            <Link to="/master-projects" className="hover:underline truncate">{m.project_name}</Link>
            {m.client && <><span>·</span><span className="truncate">{m.client}</span></>}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-2 text-[11px]">
            <StatusChip status={m.status} size="sm" />
            {m.target_date ? (
              <span className={`inline-flex items-center gap-1 ${overdue ? 'text-[hsl(var(--danger))] font-medium' : 'text-[hsl(var(--foreground-muted))]'}`}>
                <CalendarClock size={10} />{m.target_date}
                {m.days_until_target != null && (
                  <span>
                    ({m.days_until_target < 0
                      ? `${Math.abs(m.days_until_target)}d overdue`
                      : m.days_until_target === 0 ? 'today' : `in ${m.days_until_target}d`})
                  </span>
                )}
              </span>
            ) : (
              <span className="chip status-cancelled" style={{ fontSize: '10.5px' }}>No date</span>
            )}
          </div>
        </div>
        {overdue ? (
          <AlertTriangle size={14} className="text-[hsl(var(--danger))] flex-none mt-0.5" />
        ) : (
          <CheckCircle2 size={14} className="text-[hsl(var(--foreground-subtle))] flex-none mt-0.5" />
        )}
      </div>
      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t" style={{ borderColor: 'hsl(var(--border))' }}>
        <Button size="sm" variant="outline" className="h-6 px-2 text-[11.5px]" disabled={busy}
                onClick={onDone}
                data-testid={`my-milestone-done-${m.project_id}-${m.milestone_index}`}>
          <CheckCircle2 size={11} className="mr-1" />Done
        </Button>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-[11.5px]" disabled={busy}
                onClick={onShift}
                data-testid={`my-milestone-shift-${m.project_id}-${m.milestone_index}`}>
          <ArrowRight size={11} className="mr-1" />Shift
        </Button>
      </div>
    </div>
  );
}

function ShiftMilestoneDialog({ m, reasons, onClose, onDone }) {
  const [newDate, setNewDate] = useState(m.target_date || todayISO());
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const canSave = !!newDate && !!reason;

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/master-projects/${m.project_id}/milestones/act`, {
        milestone_index: m.milestone_index,
        milestone_name: m.milestone_name,
        action: 'shift',
        new_target_date: newDate,
        reason,
      });
      toast.success('Milestone target shifted.');
      onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="milestone-shift-dialog">
        <DialogHeader>
          <DialogTitle>Shift milestone target</DialogTitle>
          <DialogDescription>
            You’re moving <b>&ldquo;{m.milestone_name}&rdquo;</b> on <b>{m.project_name}</b>.
            Pick <i>why</i> it slipped — this feeds the week-over-week root-cause trend leadership sees.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">
            Current target: <b>{m.target_date || 'not set'}</b>
            {m.is_overdue && <span className="text-[hsl(var(--danger))] ml-1">· overdue</span>}
          </div>
          <div>
            <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">New target date</div>
            <Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} data-testid="milestone-shift-date" />
          </div>
          <div>
            <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">Why did it slip? (required)</div>
            <select className="h-9 w-full border rounded-md px-2 text-[14px]"
                    style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-0))' }}
                    value={reason} onChange={e => setReason(e.target.value)}
                    data-testid="milestone-shift-reason">
              <option value="">— Pick a reason —</option>
              {reasons.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!canSave || saving} data-testid="milestone-shift-save">
            {saving ? 'Saving…' : 'Shift target'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
